import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import type { BaseCategory, UserDecision } from "@usercentrics/cmp-browser-sdk";
import { ReactNode, useMemo, useState } from "react";

import { ConsentSettingsDialog } from "./ConsentSettingsDialog.tsx";
import { isCategoryEnabled, orderCategories } from "./categories.ts";
import { htmlToParagraphs } from "./htmlToParagraphs.ts";
import type { UsercentricsCmp } from "./useUsercentricsCmp.ts";

type ConsentScreenProps = {
  cmp: UsercentricsCmp;
  logo: ReactNode;
  /** Rendered centered behind the banner card. */
  backdrop?: ReactNode;
  /** Called once the user has accepted, denied or saved a decision. */
  onDecision: () => void;
};

/** First consent layer: a banner card anchored bottom-right, second layer in a dialog. */
export function ConsentScreen({
  cmp,
  logo,
  backdrop,
  onDecision,
}: ConsentScreenProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const visibleCategories = useMemo(
    () =>
      orderCategories(cmp.categories.filter((category) => !category.isHidden)),
    [cmp.categories],
  );

  const services = useMemo(
    () => visibleCategories.flatMap((category) => category.services),
    [visibleCategories],
  );

  const [serviceConsents, setServiceConsents] = useState<
    Record<string, boolean>
  >(() =>
    Object.fromEntries(
      services.map((service) => [
        service.id,
        service.isEssential || service.consent.status,
      ]),
    ),
  );

  const setCategoryConsent = (category: BaseCategory, enabled: boolean) =>
    setServiceConsents((prev) => {
      const next = { ...prev };
      category.services.forEach((service) => {
        next[service.id] = service.isEssential ? true : enabled;
      });
      return next;
    });

  const runDecision = async (action: () => Promise<void>) => {
    if (submitting) {
      return;
    }
    // Close the settings dialog right away; the decision submits in the background.
    setSettingsOpen(false);
    setSubmitting(true);
    try {
      await action();
      onDecision();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = () =>
    runDecision(() => {
      // Essential services are already pinned to `true` in state.
      const decisions: UserDecision[] = services.map((service) => ({
        serviceId: service.id,
        status: Boolean(serviceConsents[service.id]),
      }));
      return cmp.save(decisions);
    });

  const { labels } = cmp;
  const privacyLink = labels?.links.privacyPolicy;
  const imprintLink = labels?.links.imprint;
  const denyNonEssentialLabel = labels?.buttons.denyAll;

  const descriptionParagraphs = useMemo(
    () => htmlToParagraphs(labels?.firstLayer.description.default ?? ""),
    [labels],
  );

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        bgcolor: "background.main",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        p: { xs: 2, sm: 4 },
      }}
    >
      {backdrop && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {backdrop}
        </Box>
      )}
      <Card
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 550,
          maxHeight: "100%",
          overflowY: "auto",
          boxShadow: (theme) => theme.customShadows.modal,
        }}
      >
        <CardContent sx={{ p: 4, m: 0 }}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex" }}>{logo}</Box>
            <Typography variant="h2" component="h1">
              {labels?.firstLayer.title}
            </Typography>
            {descriptionParagraphs.map((paragraph, index) => (
              <Typography key={index} variant="body2">
                {paragraph}
              </Typography>
            ))}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {privacyLink?.url && privacyLink.label && (
                <Button
                  variant="text"
                  size="small"
                  href={privacyLink.url}
                  target="_blank"
                  rel="noopener"
                  sx={{ whiteSpace: "normal", textAlign: "left" }}
                >
                  {privacyLink.label}
                </Button>
              )}
              {imprintLink?.url && imprintLink.label && (
                <Button
                  variant="text"
                  size="small"
                  href={imprintLink.url}
                  target="_blank"
                  rel="noopener"
                  sx={{ whiteSpace: "normal", textAlign: "left" }}
                >
                  {imprintLink.label}
                </Button>
              )}
              {labels?.buttons.showSecondLayer && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setSettingsOpen(true)}
                  sx={{ whiteSpace: "normal", textAlign: "left" }}
                >
                  {labels.buttons.showSecondLayer}
                </Button>
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{ pt: 1 }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={() => runDecision(() => cmp.denyNonEssential())}
                sx={{ flex: "1 1 auto", whiteSpace: "normal" }}
              >
                {denyNonEssentialLabel}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={() => runDecision(() => cmp.acceptAll())}
                sx={{ flex: "1 1 auto", whiteSpace: "normal" }}
              >
                {labels?.buttons.acceptAll}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Mounted only while open so a pending/failed submit never leaves an invisible modal behind. */}
      {settingsOpen && (
        <ConsentSettingsDialog
          open
          onClose={() => setSettingsOpen(false)}
          labels={labels}
          categories={visibleCategories}
          isCategoryEnabled={(category) =>
            isCategoryEnabled(category, serviceConsents)
          }
          onToggleCategory={setCategoryConsent}
          submitting={submitting}
          onDenyNonEssential={() => runDecision(() => cmp.denyNonEssential())}
          onSave={handleSave}
          onAcceptAll={() => runDecision(() => cmp.acceptAll())}
        />
      )}
    </Box>
  );
}
